/**
 * installDashboardHost — the one seam where HQ wires @intrasync/dashboard
 * into its composition root (called from createRootStore.ts right after the
 * RootStore is constructed).
 *
 * Three things happen here, in this order, and each has a reason to be here
 * rather than anywhere else:
 *
 * 1. `registerDashboardClientMethods(app)` declares the dashboard's custom
 *    Feathers methods (`boards.duplicate`, `widget-instances.ensureInstance`,
 *    `user-prefs.setDefaultBoard`, `data.query`) on HQ's SOCKET client. It is
 *    GUARDED for `hqMode === "test"`: under vitest `apiClient("test")` is a
 *    transportless `feathers().configure(authentication())` with no default
 *    service, so `client.service(path)` would throw. Paths the library could
 *    not declare on are reported through diagnostics, never swallowed —
 *    a skipped path means every board write is silently a 404 later.
 *
 * 2. `dashboardModule.onInit(rootStore)` creates the registry on
 *    `rootStore.dashboardRegistry` and registers the STOCK types. Safe to call
 *    synchronously: stock types declare no navigation targets.
 *
 * 3. HQ's own `hq.*` types are registered only once
 *    `rootStore.routerStore.routesInitialized` is true. The RouterStore fills
 *    its route map ASYNCHRONOUSLY after construction (RouterStore.initializeRouterData
 *    awaits setLayouts/setRoutes), so synchronously after `new RootStore()` the
 *    map is `{}` and the registry — which validates every declared target and
 *    refuses the whole batch on one miss — would throw for all ten types.
 *    mobx `when` fires exactly once; its disposer is returned so a test (or a
 *    future teardown) can cancel the pending registration.
 *
 * A registration failure is reported as an error diagnostic and does not throw
 * past boot: the board degrades to "type not installed" tiles, which is the
 * honest state, rather than a white screen.
 */
import { when } from "mobx";
import type { RootStore as LibraryRootStore } from "@intrasync/stores";
import {
  dashboardModule,
  dashboardRegistryOf,
  registerDashboardClientMethods,
  type FeathersClientLike,
} from "@intrasync/dashboard";
import { HQ_WIDGET_TYPES } from "./hqWidgetTypes";
import { SHARE_DIRECTORY_KEY, createShareDirectory } from "./shareDirectory";

/** The slice of HQ's RootStore this installer reads. Structural so a test can stub it. */
export interface DashboardHostRootStore {
  routerStore?: { routesInitialized: boolean } | undefined;
  diagnostics?: {
    warn(store: string, code: "service.init_failed", message: string, detail?: { data?: Record<string, unknown> }): void;
    error(
      store: string,
      code: "service.init_failed",
      message: string,
      detail?: { error?: unknown; data?: Record<string, unknown> },
    ): void;
  };
  [key: string]: unknown;
}

export interface InstallDashboardHostOptions {
  /**
   * The app mode the Feathers client was built for (`hqMode` in
   * rootStoreConfig.ts, passed in by createRootStore). Taken as an argument
   * rather than imported: rootStoreConfig reads `localStorage` at module scope,
   * which would make this seam un-importable under the node test runner.
   */
  mode: string;
}

const DIAG_STORE = "DashboardHost";

export function installDashboardHost(
  rootStore: DashboardHostRootStore,
  app: FeathersClientLike,
  options: InstallDashboardHostOptions,
): () => void {
  if (options.mode !== "test") {
    try {
      const skipped = registerDashboardClientMethods(app);
      if (skipped.length > 0) {
        rootStore.diagnostics?.warn(
          DIAG_STORE,
          "service.init_failed",
          `Dashboard custom methods could not be declared on ${skipped.join(", ")}; board writes on those services will fail.`,
          { data: { skipped } },
        );
      }
    } catch (error) {
      rootStore.diagnostics?.error(
        DIAG_STORE,
        "service.init_failed",
        "Dashboard custom methods could not be declared on the Feathers client.",
        { error },
      );
    }
  }

  dashboardModule.onInit(rootStore as unknown as LibraryRootStore);

  // The Share dialog's pickers (shareDirectory.ts). Installed in every mode:
  // it reads nothing until the dialog asks.
  rootStore[SHARE_DIRECTORY_KEY] = createShareDirectory(app);

  return when(
    () => rootStore.routerStore?.routesInitialized === true,
    () => {
      const registry = dashboardRegistryOf(rootStore);
      if (!registry) {
        rootStore.diagnostics?.error(
          DIAG_STORE,
          "service.init_failed",
          "The dashboard registry is missing after onInit; HQ widget types were not registered.",
        );
        return;
      }
      try {
        registry.register(HQ_WIDGET_TYPES);
      } catch (error) {
        const problems = (error as { problems?: string[] })?.problems ?? [];
        rootStore.diagnostics?.error(
          DIAG_STORE,
          "service.init_failed",
          "HQ widget types could not be registered; every dashboard will show them as not installed.",
          { error, data: { problems } },
        );
      }
    },
  );
}
